import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";
import { PageState } from "@/components/common/PageState";
import { AdminAnalyticsDashboard } from "@/components/pages/analytics/admin/AdminAnalyticsDashboard";
import { FrontDeskAnalyticsDashboard } from "@/components/pages/analytics/front-desk/FrontDeskAnalyticsDashboard";
import { AnalyticsPageProvider } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { getAnalyticsAccess } from "@/utils/analyticsAccess";
import { getDefaultAuthorizedRoute } from "@/utils/defaultAuthorizedRoute";

type AnalyticsPageShellProps = {
  children: React.ReactNode;
};

const AnalyticsPageShell = ({
  children,
}: AnalyticsPageShellProps): React.JSX.Element => (
  <div className="h-full w-full overflow-auto bg-white p-6">{children}</div>
);

export const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const { userProfile, isLoading } = useAuthenticatedUser();
  const { hotelId } = useParams<{ hotelId?: string }>();
  const access = getAnalyticsAccess(userProfile);

  if (isLoading) {
    return (
      <AnalyticsPageShell>
        <PageState text={t("common.loading")} />
      </AnalyticsPageShell>
    );
  }

  if (access === "admin") {
    if (hotelId) {
      return (
        <AnalyticsPageShell>
          <AnalyticsPageProvider>
            <FrontDeskAnalyticsDashboard />
          </AnalyticsPageProvider>
        </AnalyticsPageShell>
      );
    }

    return (
      <AnalyticsPageShell>
        <AnalyticsPageProvider allowFutureDates={false}>
          <AdminAnalyticsDashboard />
        </AnalyticsPageProvider>
      </AnalyticsPageShell>
    );
  }

  if (access === "frontDesk") {
    return (
      <AnalyticsPageShell>
        <AnalyticsPageProvider>
          <FrontDeskAnalyticsDashboard />
        </AnalyticsPageProvider>
      </AnalyticsPageShell>
    );
  }

  return <Navigate replace to={getDefaultAuthorizedRoute(userProfile)} />;
};

export const FrontDeskAnalyticsRouteGuard: React.FC<
  AnalyticsPageShellProps
> = ({ children }) => {
  const { t } = useTranslation();
  const { userProfile, isLoading } = useAuthenticatedUser();

  if (isLoading) {
    return (
      <AnalyticsPageShell>
        <PageState text={t("common.loading")} />
      </AnalyticsPageShell>
    );
  }

  if (getAnalyticsAccess(userProfile) !== "frontDesk") {
    return <Navigate replace to="/analytics" />;
  }

  return <>{children}</>;
};
